import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles, type Article } from '@/mocks/categories';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);

  useEffect(() => {
    const found = articles.find((a) => a.id === id) || null;
    setArticle(found);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/categories"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors no-underline"
            >
              <i className="ri-arrow-left-line"></i>
              返回分类导航
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const activeCategory = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: 'KAIYUN导航',
    },
    publisher: {
      '@type': 'Organization',
      name: 'KAIYUN导航',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://kaiyun-navi.com/articles/${article.id}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <SEOHead
        title={`${article.title} - KAIYUN导航`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://kaiyun-navi.com/articles/${article.id}`}
        ogType="article"
      />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Breadcrumb & Header */}
          <section className="bg-background-50 border-b border-background-200">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类导航', href: '/categories' },
                  { label: activeCategory?.name || '', href: `/categories?tab=${article.categoryId}` },
                  { label: article.title },
                ]}
              />
              <div className="mt-4">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">
                    {activeCategory?.name}
                  </span>
                  <span className="text-xs text-foreground-400">{article.readTime}</span>
                  <span className="text-xs text-foreground-400">
                    <i className="ri-calendar-line mr-1"></i>
                    {article.publishDate}
                  </span>
                </div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 font-[var(--font-heading)]">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 mt-3">
                  {article.summary}
                </p>
              </div>
            </div>
          </section>

          {/* Article Content */}
          <section className="py-8 md:py-10 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <article className="prose prose-sm md:prose-base max-w-none">
                <div
                  dangerouslySetInnerHTML={{ __html: article.content }}
                  className="text-foreground-800 leading-relaxed [&_h2]:text-lg [&_h2]:md:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:font-[var(--font-heading)] [&_p]:text-sm [&_p]:md:text-base [&_p]:text-foreground-700 [&_p]:mb-4 [&_p]:leading-relaxed [&_strong]:text-foreground-900"
                />
              </article>

              {/* Tags */}
              <div className="mt-8 pt-6 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-8 md:py-12 bg-background-100">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 font-[var(--font-heading)]">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/articles/${rel.id}`}
                      className="group p-4 bg-background-50 border border-background-200 rounded-lg hover:border-primary-200 transition-all cursor-pointer no-underline"
                    >
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2">
                        {rel.summary}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}